import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslationService } from '../../common/services/languages/translation.service';
import { MatDialog } from '@angular/material';
import { select } from '@angular-redux/store';
import { Observable, Subscription } from 'rxjs';
import { CommonDispatcher } from '../../store/common/common-dispatcher';
import { UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ApiResponseState } from '../../store/api-response/api-response-state.model';
import { OrchestrationService } from '../services/orchestration-service/orchestration.service';
import { ApiResponseActions } from '../../store/api-response/api-response-actions';
import { AuthDispatcher } from '../../store/auth/auth-dispatcher';
import { regex } from '../../common/regex.constants';
import { VehicleModel } from '../vcm/models/vehicle.model';
import { VehicleDispatcher } from '../../store/vehicle-store/vehicle-dispatcher';
import { ApiResponseDispatcher } from '../../store/api-response/api-response-dispatcher';
import { vehicleProperties, headUnitProperties, dcmInfoProperties } from './properties/vehicle-properties';
import { DialogBoxComponent } from '../../common/components/dialog-box/dialog-box.component';
import { FinegrainRolesService } from '../../common/services/finegrain-roles/finegrain-roles.service';
import { chipIdValidator } from '../../common/helpers/validators';
import { SnackBarService } from '../../common/services/snack-bar/snack-bar.service';
import { MatFormField, MatLabel, MatSuffix, MatError, MatHint } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { NgIf, NgFor } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MatCard } from '@angular/material/card';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-vcm',
    templateUrl: './vcm.component.html',
    styleUrls: ['./vcm.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, MatFormField, MatLabel, MatInput, NgIf, MatIcon, MatSuffix, MatError, MatHint, MatCheckbox, MatButton, MatCard, NgFor, TranslatePipe]
})
export class VcmComponent implements OnInit, OnDestroy {
  /**
 * @property Will contain vehicle details payload
 */
  public vehicleDetails: object;
  /**
 * @property string that holds the vehicle description
 */
  public vehicle_description: string;
  /**
 * @property Property to store the unique capabilities
 */
  public uniqueCapabilities: any[];
  /**
 *@property filtered capabilities
 **/
  public sortedCapabilities: any;
  /**
 * @property vehicleProperties
 */
  public vehicleProperties: Array<object> = vehicleProperties;
  /**
 * @property filteredVehicleProperties - filtered vehicle properties based on generation
 */
  public filteredVehicleProperties: Array<object> = vehicleProperties;
  /**
 * @property headUnitProperties
 */
  public headUnitProperties: Array<object> = headUnitProperties;
  /**
   * @property dcmInfoProperties
   */
  public dcmInfoProperties: Array<object> = dcmInfoProperties;

  /**
   * @property vehicleBrand propery to store the vehicle brand
   */
  public vehicleBrand: string;

  /**
   * @property dcmHeadUnitSection property is the concat of both head unit and dcm info
   */
  public dcmHeadUnitSection = [
    {
      'label': 'dcm-info',
      'properties': dcmInfoProperties
    },
    {
      'label': 'head-unit',
      'properties': headUnitProperties
    }
  ];
  /**
    *@var vinStatus to store  vin status
    **/
  public vinStatus: string;
  /**
  *@var vinHistoryStatus to store  vin status
  **/
  public vinHistoryStatus: string;

  /**
  *@var isClearVin to store  vin status flag
  **/
  public isClearVin: boolean;
  /**
  *@var vinStatus to store VIN status for 21MM vehicles
  **/
  public isVinSelected: boolean;
  /**
  *@var chipIdStatus to store chip ID status for 24MM vehicles
  **/
  public chipIdStatus: string;
  /**
  *@var isChipIdSelected to store checkbox selection state for 24MM vehicles
  **/
  public isChipIdSelected: boolean;
  /**
    *@var search_attribute to store  input field email or phone or vin
    **/
  public search_attribute: UntypedFormGroup;
  /**
   *@var model to store the model of Vehicle
   **/
  public model: any;
  /**
  *@arg apiObservable   observe api variable in redux
  **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  /**
   * @property subscription  Property to store the api subscription
   */
  public subscription: Subscription;
  /**
  * @property Property to store the api subscription
  */
  public apiSubscription: Subscription;
  /**
  * @property hasHistory flag to check vin history
  */
  public hasHistory: boolean;
  /**
  * @property hasHistory flag to check show vehicle error
  */
  public isNotFound: boolean;
  /**
  * @property vcmHistory to store vcm history
  */
  public vcmHistory: object;
  /**
  * @property triedVehicleApiForChipId flag to track if vehicle API was attempted for chip ID search
  */
  private triedVehicleApiForChipId: boolean;
  constructor(
    public fb: UntypedFormBuilder,
    public translate: TranslationService,
    private apiDispatcher: ApiResponseDispatcher,
    public vehicleDispatcher: VehicleDispatcher,
    public orch: OrchestrationService,
    public common: CommonDispatcher,
    public authDispatcher: AuthDispatcher,
    public dialog: MatDialog,
    private fineGrainService: FinegrainRolesService,
    private snackBarService: SnackBarService,
  ) {
    this.apiSubscription = this.apiObservable.subscribe((api) => {
      if (api.apiAction === ApiResponseActions.SET_VCM_TOKEN) {
        if (api.vcmTokenResponse['type'] === 'success') {
          const decodedToken = api.vcmTokenResponse['access_token'];
          this.authDispatcher.set21mmToken(decodedToken);
        }
      }
    });
  }
  /**
   * @function ngOnInit Method to Initializes the component and perform initial operations
   * @returns void
   */
  ngOnInit(): void {
    this.defaults();
    this.search_attribute = this.fb.group(this.bindings);
    this.subscription = this.apiObservable.subscribe((api) => {
      const temp = api.apiAction === ApiResponseActions.SET_FIND_VEHICLE && api.findVehicle['type'] === 'success' && !!api.findVehicle['payload']['vehicle']
        && (!!!api.vinlist['payload'] || !!!api.vinlist['payload'][0]) ?
        this.findVehicleSuccess(api.findVehicle) : api.findVehicle['type'] === 'error' ? this.errorResponse(api.findVehicle, 'vehicle') : null;
      const tempVcmHistory = api.apiAction === ApiResponseActions.SET_VCM_HISTORY && api.vcmHistoryResponse['type'] === 'success' ?
        (!!api.vcmHistoryResponse['vehicle'] ? this.vcmHistorySuccess(api.vcmHistoryResponse) : this.handleVcmHistoryNoData(api.vcmHistoryResponse)) : 
        api.vcmHistoryResponse['type'] === 'error' ? this.errorResponse(api.vcmHistoryResponse, 'history') : null;
      const tempClearVin = api.apiAction === ApiResponseActions.SET_CLEAR_VIN && api.vcmClearResponse['type'] === 'success' && !!api.vcmClearResponse ?
        this.findVehicle() : api.vcmClearResponse['type'] === 'error' ? this.errorResponse(api.vcmClearResponse, 'clear') : null;
      const tempblackList = api.apiAction === ApiResponseActions.SET_BLACKLIST_VIN && api.vcmBlacklistResponse['type'] === 'success' && !!api.vcmBlacklistResponse ?
        this.findVehicle() : api.vcmBlacklistResponse['type'] === 'error' ? this.errorResponse(api.vcmBlacklistResponse, 'black') : null;
      const tempwhiteList = api.apiAction === ApiResponseActions.SET_CLEAR_VIN && api.vcmClearResponse['type'] === 'success' && !!api.vcmClearResponse ?
        this.search() : api.vcmClearResponse['type'] === 'error' ? this.errorResponse(api.vcmClearResponse, 'black') : null;
      const tempClearChipId = api.apiAction === ApiResponseActions.SET_CLEAR_CHIP_ID && api.vcmClearChipIdResponse['type'] === 'success' && !!api.vcmClearChipIdResponse ?
        this.handleClearChipIdSuccess() : api.vcmClearChipIdResponse['type'] === 'error' ? this.errorResponse(api.vcmClearChipIdResponse, 'clearChipId') : null;
    });
  }
  /**
   * @ngOnDestroy unsubscribes subscription and apiSubscription on ngDestroy
   * @returns void
   **/
  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.apiSubscription.unsubscribe();
  }

  /**
   * @function search creates the model- vehicle or customer
   * @param $event input object
   * @returns void
   **/
  public search(): void {
    this.common.resetAll();
    this.createModel();
    this.searchProfile();
  }
  /**
   * @function searchProfile Initiates the method calls to search the respective profile
   * @returns void
   **/
  public searchProfile(): void {
    this.model.error = false;
    this.defaults();
    this.findVehicle();
  }

  /**
   * @function findVehicle action to find vehicle (api call)
   * @returns void
   **/
  public findVehicle(): void {
    this.vehicleDetails = {};
    if (this.model.isChipIdSearch()) {
      this.orch.getVcmHistory(undefined, this.model.chipId, true);
    } else if (this.model.vin && this.model.vin.length === 17) {
      this.orch.findVehicle(this.model.vin);
    } else {
      this.orch.getVcmHistory(this.model.vin);
    }
  }
  /**
   * @function findVehicleSuccess success of find vehicle api
   * @param {object} response response from the api
   **/
  public findVehicleSuccess(response: object): void {
    this.setVehicleDetails(response);
    this.apiDispatcher.setfindVehicleResponse({});
  }
/**
 * Main handler for setting vehicle details from API response.
 * Delegates capability handling and supported/unsupported generation logic.
 *
 * @param {any} response - API response containing payload.vehicle data.
 * @returns {void}
 */
public setVehicleDetails(response: any): void {
  const vehicle = response && response.payload && response.payload.vehicle;
  if (!vehicle) return;

  this.model.vehicleDetails = vehicle;
  this.vehicleDetails = vehicle;

  const generation = vehicle['generation'];
  const isSupportedGeneration = generation === '21MM' || generation === '24MM';

  this.vehicleBrand = this.model.getVehicleBrand(vehicle, false);
  this.vehicle_description =
    vehicle['modelDescription'] ||
    (vehicle['model'] && vehicle['model']['modelDescription']);

  if (vehicle['capabilities']) {
    this.getCapabilities(vehicle['capabilities']);
  }
  this.filterVehicleProperties(generation);

  this.handleGenerationFlow(vehicle, generation, isSupportedGeneration);
}

/**
 * Handles generation-based logic including unsupported generations,
 * VCM history fetching, merging, and HU/Chip assignment.
 *
 * @param {any} vehicle - The extracted vehicle object.
 * @param {string} generation - Vehicle generation (e.g., 21MM / 24MM).
 * @param {boolean} isSupportedGeneration - True if generation is supported.
 * @returns {void}
 */
private handleGenerationFlow( vehicle: any, generation: string, isSupportedGeneration: boolean ): void {

  if (!isSupportedGeneration) {
    this.nonSupportedGeneration();
    return;
  }

  if (!this.hasHistory && generation === '21MM') {
    this.orch.getVcmHistory(vehicle['vin']);
    return;
  }

  if (generation === '21MM') {
    const brand = this.model.getVehicleBrand(vehicle, true);
    const vin = vehicle['vin'];
    
    const historyVehicleData = this.vcmHistory && this.vcmHistory['vehicle'] ? this.vcmHistory['vehicle'] : {};
    const chipId = historyVehicleData['chipId'] || '';
    const huId = historyVehicleData['huId'] || '';
    
    if (!vehicle['vehicle']) {
      vehicle['vehicle'] = {};
    }
    Object.assign(vehicle['vehicle'], historyVehicleData, {
      hucomHeader: brand +'; ' + vehicle['vin'] + '; ' + vehicle['imei'] + '; ' + vehicle['region'] + '; ' + generation
    });
    
    if (this.vcmHistory['Status']) {
      vehicle['Status'] = this.vcmHistory['Status'];
    }
    return;
  }

  const rawChipId = vehicle['headUnit'] && vehicle['headUnit']['headUnitSecurityChipId'];
  const chipId = rawChipId && /^0+$/.test(rawChipId) ? '' : rawChipId;
  const huId = vehicle['dcm'] && vehicle['dcm']['vehicleUnitTerminalNumber'];
  const brand = this.model.getVehicleBrand(vehicle, true);
  const vin = vehicle['vin'];
  this.hasHistory = true;
  Object.assign(vehicle, {
    vehicle: {
      chipId,
      huId,
      vin,
      hucomHeader: brand +'; ' + vehicle['vin'] + '; ' + vehicle['imei'] + '; ' + vehicle['region'] + '; ' + generation
    }
  });
}

  /**
   * @function defaults sets the default redux
   * @returns void
   **/
  public defaults(): void {
    this.hasHistory = false;
    this.isNotFound = false;
    this.isChipIdSelected = false;
    this.isVinSelected = false;
    this.triedVehicleApiForChipId = false;
    this.resetStores();
  }

  /**
   * @function resetStores reset vehicle and vcmHistory stores
   * @returns void
   **/
  public resetStores(): void {
    this.vehicleDispatcher.resetVehicleStore();
    this.apiDispatcher.setVcmHistory({});
  }
  
  /**
 * @function vcmHistorySuccess populates vehicleDetails with vcmHistory vehicle payload
 * @returns void
 */
  public vcmHistorySuccess(record: object): void {
    this.vcmHistory = record;
    if (!!!this.model.vehicleDetails) {
      this.orch.findVehicle(record['vehicle']['vin']);
    }
    this.hasHistory = true;
    this.vinHistoryStatus = record['Status'];
    if (record['vehicle']['accessBlocked'] !== undefined) {
      this.isClearVin = record['vehicle']['accessBlocked'];
      record['vehicle']['accessBlocked'] = record['vehicle']['accessBlocked'].toString();
      this.vcmHistory = record;
    } else {
      this.isClearVin = false;
    }
    if (this.vehicleDetails) { Object.assign(this.vehicleDetails, this.vcmHistory); }
  }

  /**
   * @function handleVcmHistoryNoData handles when history API returns 200 but no vehicle data
   * @param {object} response response from the api
   * @returns void
   **/
  public handleVcmHistoryNoData(response: object): void {
    this.hasHistory = false;
    this.isNotFound = true;
    this.apiDispatcher.setVcmHistory({});
  }

  /**
  * @function getCapabilities method to get the translation of the capability
  * @returns void
  */
  public getCapabilities(vehicleCapabilities: Array<any>): void {
    let hideCapabilities = ['RemoteAuthorizable', 'NavigationTrafficProbe', 'IpBasedTrafficAndWeather', 'RemoteCustom', 'DynamicPoi', 'AlternativeFuel'];
    vehicleCapabilities.forEach(element => {
      if (element.name !== 'Can300' && !element.displayName) {
        hideCapabilities.push(element.name);
      }
    });
    if (!!this.vehicleDetails['generation'] && this.vehicleDetails['generation'].toLowerCase() === '17cyplus') {
      hideCapabilities = [...hideCapabilities, 'appSuiteCapable'];
    }
    const seenDisplayNames = new Set<string>();
    this.uniqueCapabilities = vehicleCapabilities
      .filter(capability => (capability.name || capability.displayName) && hideCapabilities.indexOf(capability.name) < 0)
      .filter(capability => capability.name === 'Can300' || capability.value === true)
      .filter(capability => {
        const displayKey = capability.displayName || (capability.translation && capability.translation.english);
        if (!displayKey) {
          return capability.name === 'Can300';
        }
        if (seenDisplayNames.has(displayKey)) {
          return false;
        }
        seenDisplayNames.add(displayKey);
        return true;
      });
    this.sortedCapabilities = this.uniqueCapabilities.map(capability => {
      const capabilityDisplay = capability['name'] !== 'Can300' ? capability['translation'] && capability['translation']['english'] || capability['displayName'] : capability['name'];
      const capabilityValue = capability['value'];
      return { capabilityDisplay, capabilityValue };
    });
  }

  /**
  * @function filterVehicleProperties filters out access-blocked, access-update, access-updated and cert-mapped properties for 24MM vehicles
  * @param {string} generation - Vehicle generation
  * @returns void
  */
  public filterVehicleProperties(generation: string): void {
    if (generation === '24MM') {
      this.filteredVehicleProperties = this.vehicleProperties.filter(
        (property: any) => property.label !== 'access-blocked' && 
                           property.label !== 'access-update' && 
                           property.label !== 'access-updated' && 
                           property.label !== 'cert-mapped'
      );
    } else {
      this.filteredVehicleProperties = this.vehicleProperties;
    }
  }

  /**
 * @function getSubproperty function to show nested properties
 * @returns string
 */
  public getSubproperty(pathList, obj): string {
    const propertyMap = pathList.map(path => {
      const properties = Array.isArray(path) ? path : path.split('.');
      const propertyReducer = properties.reduce((prev, curr) => prev && prev[curr], obj);
      return propertyReducer;
    });
    let value = propertyMap.filter(property => property)[0];
    if (pathList.includes('vehicle.chipId') && value && /^0+$/.test(value)) {
      return '';
    }
    
    return value;
  }
  /**
   * @function bindings returns the defaults
   * @returns void
   **/
  public get bindings(): object {
    return {
      searchAttribute: ['',
        [Validators.required,
        Validators.maxLength(50),
        Validators.pattern(regex.vinImeiChipIdPattern),
        chipIdValidator]]
    };
  }
  /**
   * @function onKeyUp updates the form value
   * @returns void
   **/
  public onKeyUp(): void {
    let value = this.search_attribute.controls.searchAttribute.value.replace(/ /g, '').trim();
    if (value.length === 10 || value.length === 12) {
      value = value.toUpperCase();
    }
    this.search_attribute.controls.searchAttribute.setValue(value);
    this.search_attribute.controls.searchAttribute.updateValueAndValidity();
  }

  /**
   * @function createModel creates the model- vehicle or customer
   * @returns void
   **/
  public createModel(): void {
    this.model = new VehicleModel(this.search_attribute.controls.searchAttribute.value);
  }
  /**
   * @function nonSupportedGeneration enables error message for non 21 MM vehicles
   * @returns void
   **/
  public nonSupportedGeneration(): void {
    this.isNotFound = true;
    this.model.error = true;
  }
  /**
   * @function errorResponse Error of find vehicle api
   * @returns void
   **/
  public errorResponse(error: object, type: string): void {
    const temp = type === 'history' ? this.hasHistory = false : '';
    
    // If history API fails for chip ID search, try vehicle API (for 24MM vehicles)
    // This handles the case where chip ID exists only in vehicle API (24MM)
    if (type === 'history' && this.model.isChipIdSearch() && !this.triedVehicleApiForChipId) {
      this.isNotFound = false;
      this.triedVehicleApiForChipId = true;
      this.apiDispatcher.setVcmHistory({});
      this.orch.findVehicle(undefined, this.model.chipId);
      return;
    }
    
    this.isNotFound = true;
    const checkType = type === 'vehicle' ? this.apiDispatcher.setfindVehicleResponse({}) : (type === 'history' ? this.apiDispatcher.setVcmHistory({}) :
      (type === 'clear' ? this.apiDispatcher.setClearVin({}) : (type === 'black' ? this.apiDispatcher.setBlackListVin({}) : 
      (type === 'clearChipId' ? this.apiDispatcher.setClearChipId({}) : ''))));
    if (error['errorCode'] === 'CVS-0002') {
      this.model.error = false;
    }
  }

  /**
   * @function validateVin calls respective endpoint based on user action
   * @returns void
   **/
  public validateVin(): void {
    const vin = this.vehicleDetails && this.vehicleDetails['vin'] ? this.vehicleDetails['vin'] : this.model.vin;
    this.orch.clearVin(vin);
    this.vinStatus = '';
  }

  /**
   * @function validateChipId calls respective endpoint to clear chip ID for 24MM vehicles
   * @returns void
   **/
  public validateChipId(): void {
    const chipId = this.vehicleDetails['headUnit'] && this.vehicleDetails['headUnit']['headUnitSecurityChipId'];
    const vin = this.vehicleDetails['vin'];
    if (chipId && vin) {
      this.orch.clearChipId(chipId, vin);
    }
    this.chipIdStatus = '';
    this.isChipIdSelected = false;
  }

  /**
   * @function onVinCheckboxChange handles checkbox state change for VIN unmapping (21MM vehicles)
   * @param {any} event - checkbox change event
   * @returns void
   **/
  public onVinCheckboxChange(event: any): void {
    this.vinStatus = event.checked ? 'clear_vin' : '';
  }

  /**
   * @function onChipIdCheckboxChange handles checkbox state change for chip ID unmapping
   * @param {any} event - checkbox change event
   * @returns void
   **/
  public onChipIdCheckboxChange(event: any): void {
    this.chipIdStatus = event.checked ? 'clear_chip_id' : '';
  }

  /**
   * @function handleClearChipIdSuccess handles successful chip ID clearing
   * If search was by chip ID, search by VIN instead to avoid 404 error
   * @returns void
   **/
  public handleClearChipIdSuccess(): void {
    const message = this.translate.getCustomTranslation('vcm.messages.chip-id-cleared-success')['value'];
    this.snackBarService.showToast(message, '', 5);

    if (this.model.isChipIdSearch() && this.vehicleDetails && this.vehicleDetails['vin']) {
      // Search was by chip ID, now search by VIN to show updated vehicle info
      const vin = this.vehicleDetails['vin'];
      this.model.vin = vin;
      this.model.searchType = 'vin';
      this.model.chipId = undefined;
      this.orch.findVehicle(vin);
    } else {
      // Search was by VIN, refresh normally
      this.findVehicle();
    }
  }

  /**
   * @function clear clear action
   * @returns void
   **/
  public clear(): void {
    this.search_attribute.reset();
  }

  /**
 * @method openDialog will display a dialog box to go back to dashboard or confirmation dialog for approve or reject
 * @returns void
 */
  public openDialog(status: string): void {
    const dialogRef = this.dialog.open(DialogBoxComponent, {
      data: {
        type: status
      },
      width: '28em',
      height: 'auto'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (status === 'clear_chip_id') {
          this.validateChipId();
        } else {
          this.validateVin();
        }
      }
    });
  }
  /**
  * @method isAuthorizedVcmAdminPlus to check wehter it is authorized admin plus
  * @returns true if it is authorized vcm admin plus user
  **/
  public isAuthorizedVcmAdminPlus(): boolean {
    return this.fineGrainService.getPortalPermissions('vcm_admin_plus') ? false : !this.isClearVin;
  }

  /**
   * @method isChipIdEmpty checks if the chipId is empty for 21MM and 24MM vehicles
   * @returns true if chipId is empty or not present
   **/
  public isChipIdEmpty(): boolean {
    if (this.vehicleDetails && (this.vehicleDetails['generation'] === '24MM' || this.vehicleDetails['generation'] === '21MM')) {
      const chipId = this.vehicleDetails['vehicle'] && this.vehicleDetails['vehicle']['chipId'];
      return !chipId || chipId === '';
    }
    return false;
  }

}
